'use client';

import './slider-input.scss';
import { Range as RdxRange, Root as RdxRoot, Thumb as RdxThumb, Track as RdxTrack } from '@radix-ui/react-slider';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Either } from '@sola/schema/dotcom/helpers.ts';
import { trackClick } from '@sola/tracking/components/datalayer.tsx';
import { cn } from '../../utils/helpers.ts';
import { Field, FieldProps } from '../field/field.tsx';
import { NumberField } from '../number-field/number-field.tsx';
interface SliderInputWithLabel {
  label: string;
}
interface SliderInputWithoutLabel {
  'aria-labelledby': React.ComponentPropsWithoutRef<'input'>['aria-labelledby'];
}
export type SliderInputProps = Partial<Either<SliderInputWithLabel, SliderInputWithoutLabel> & Omit<FieldProps, 'children' | 'required' | 'max' | 'min' | 'pattern' | 'valueAsNumber' | 'valueAsDate' | 'maxLength' | 'minLength' | 'validate' | 'value' | 'setValueAs' | 'deps' | 'isFocused'>> & {
  min: number;
  max: number;
  step?: number;
  value?: number[];
  label?: string;
  prefix?: string;
  name?: string;
  disabled?: boolean;
  orientation?: 'horizontal' | 'vertical';
  sliderInputRef?: React.MutableRefObject<HTMLInputElement>;
  showThumbLabels?: boolean;
  showInputForm?: boolean;
  onCommit?: (sliderRange: {
    newValue: number[];
  }) => void;
};
export const SliderInput: React.FC<SliderInputProps> = ({
  'aria-labelledby': ariaLabelledBy,
  min,
  max,
  step = 1,
  value: startingValue,
  label,
  prefix,
  disabled,
  orientation = 'horizontal',
  sliderInputRef,
  onChange,
  onCommit,
  showThumbLabels,
  showInputForm,
  ...fieldProps
}) => {
  const {
    t
  } = useTranslation(['core']);
  const [sliderValue, setSliderValue] = React.useState<number[]>(startingValue || [min, max]);

  // since Chrome doesn't always trigger if the values overlap, we have to manually fire
  // the onCommit when the onPointerLeave fires. However, to be efficient, we only want onCommit
  // to fire if the slider values have changed since the last onCommit.
  const [shouldCommit, setShouldCommit] = React.useState(false);
  const minInputRef = useRef<HTMLInputElement>({} as HTMLInputElement);
  const maxInputRef = useRef<HTMLInputElement>({} as HTMLInputElement);
  let blurTimeout: ReturnType<typeof setTimeout> | undefined;
  const handleInputChange = () => {
    if (blurTimeout) {
      clearTimeout(blurTimeout);
    }
    blurTimeout = setTimeout(() => {
      if (minInputRef.current) {
        minInputRef.current.blur();
      }
      if (maxInputRef.current) {
        maxInputRef.current.blur();
      }
    }, 2000);
  };
  useEffect(() => {
    if (startingValue) {
      setSliderValue(startingValue);
    }
  }, [startingValue]);
  return <Field {...fieldProps as FieldProps} value={startingValue || [min, max]} controlRef={sliderInputRef}>
      {({
      controlProps,
      formMethods
    }) => {
      const handleValueChange = (value: number[]) => {
        formMethods?.setValue(controlProps.name as string, value);
        setSliderValue(value);
        if (onChange) {
          onChange({
            newValue: value
          });
        }
        setShouldCommit(true);
      };
      const handleValueCommit = (value_0: number[]) => {
        formMethods?.setValue(controlProps.name as string, value_0);
        setSliderValue(value_0);
        if (onCommit) {
          onCommit({
            newValue: value_0
          });
        }
        setShouldCommit(false);
      };
      return <>
            {label && <label className="form-label label" htmlFor={controlProps.id}>
                {label}
              </label>}
            <div className="Slider">
              <RdxRoot className={cn('Slider__root flex relative items-center', {
            'Slider__root--disabled': disabled,
            'flex-col': orientation === 'vertical'
          })} defaultValue={startingValue} value={sliderValue} min={min} max={max} step={step} orientation={orientation} disabled={disabled} onClickCapture={() => {
            trackClick(`${label} - Slider`, undefined, 'Slider__root');
          }} onValueChange={(values: number[]) => {
            const [newMin, newMax] = values;
            handleValueChange(newMax !== undefined ? [newMin, newMax] : [newMin]);
          }} onValueCommit={(values_0: number[]) => {
            const [newMin_0, newMax_0] = values_0;
            handleValueCommit(newMax_0 !== undefined ? [newMin_0, newMax_0] : [newMin_0]);
          }} onPointerLeave={() => shouldCommit && handleValueCommit(sliderValue)}>
                <RdxTrack className="Slider__track relative grow-1">
                  <RdxRange className="Slider__range absolute" />
                </RdxTrack>
                <RdxThumb className="Slider__thumb block">
                  {showThumbLabels && <span className="Slider__thumb-label label label-moderate">
                      {sliderValue?.[0]}
                    </span>}
                </RdxThumb>
                <RdxThumb className="Slider__thumb block">
                  {showThumbLabels && <span className="Slider__thumb-label label label-moderate">
                      {sliderValue?.[1]}
                    </span>}
                </RdxThumb>
              </RdxRoot>

              {showInputForm && <div className={cn('Slider__labels flex justify-between text-sm gap-comfy-md', {
            'mt-comfy-md': showThumbLabels,
            'mt-2': !showThumbLabels
          })}>
                  <div className="flex flex-col items-start gap-snug-xl mt-snug-lg">
                    <span className="body label-md body-neutral">
                      {t('core:slider.minimum')}
                    </span>
                    <NumberField name={`${controlProps.name}_min`} inputRef={minInputRef} inputClasses="Slider__labels-input flex items-center justify-center label-moderate label-neutral" value={sliderValue?.[0]} onChange={handleInputChange} onBlur={e => {
                const newMin_1 = Math.min(Number(e.target.value), sliderValue[1]);
                handleValueCommit([newMin_1, sliderValue[1]]);
              }} aria-label={t('core:slider.minimum')} prefix={prefix} isCurrency={false} />
                  </div>
                  <div className="flex flex-col items-end gap-snug-xl mt-snug-lg">
                    <span className="body label-md body-neutral">
                      {t('core:slider.maximum')}
                    </span>
                    <NumberField name={`${controlProps.name}_max`} inputRef={maxInputRef} inputClasses="Slider__labels-input flex items-center justify-center label-moderate label-neutral" value={sliderValue?.[1]} onChange={handleInputChange} onBlur={e_0 => {
                const newMax_1 = Math.max(Number(e_0.target.value), sliderValue[0]);
                handleValueCommit([sliderValue[0], newMax_1]);
              }} aria-label={t('core:slider.maximum')} prefix={prefix} isCurrency={false} />
                  </div>
                </div>}
            </div>
          </>;
    }}
    </Field>;
};